import { Big, type BigSource } from "big.js"
import { Decimal as PBDecimal } from "gen/proto/decimal/models_pb"

import { ensure } from "../../utils/nullish/nullish"

export class Decimal implements PBDecimal {
  str: string
  private static zeroPB = PBDecimal.create({ str: "0" })

  private constructor(decimal: PBDecimal) {
    this.str = decimal.str
  }

  public big(): Big {
    return new Big(this.str)
  }

  // zero returns a zero-valued Decimal.
  static zero(): Decimal {
    return new Decimal(Decimal.zeroPB)
  }

  // fromPB constructs a Decimal from our protobuf UUID message defined
  // in rd/proto/decimal/models.proto.
  static fromPB(pbDecimal: PBDecimal): Decimal {
    // Backend treats empty strings as zero, but Big.js can't parse them
    if (pbDecimal.str === "") {
      return Decimal.zero()
    }

    return new Decimal(pbDecimal)
  }

  // fromBigSource constructs a Decimal from a BigSource (number, string, or Big)
  static fromBigSource(source: BigSource): Decimal {
    return new Decimal({ str: new Big(source).toString() })
  }

  static fromPBOrThrow(
    pbDecimal?: PBDecimal,
    message = "Missing Decimal"
  ): Decimal {
    return Decimal.fromPB(ensure(pbDecimal, message))
  }

  static fromPBOrZero(pbDecimal?: PBDecimal): Decimal {
    if (!pbDecimal) {
      return Decimal.zero()
    }
    return Decimal.fromPB(pbDecimal)
  }

  static min(...values: Decimal[]): Decimal {
    return values.reduce((min, value) => {
      return value.lt(min) ? value : min
    }, values[0])
  }

  static max(...values: Decimal[]): Decimal {
    return values.reduce((max, value) => {
      return value.gt(max) ? value : max
    }, values[0])
  }

  // add returns a new Decimal representing the sum of this and other.
  // The original values are unmodified.
  add(other?: Decimal): Decimal {
    const b = this.big().add(other?.big() ?? new Big(0))
    return new Decimal({ str: b.toString() })
  }

  // sub returns a new Decimal representing the difference of this and
  // other. The original values are unmodified.
  sub(other?: Decimal): Decimal {
    const b = this.big().sub(other?.big() ?? new Big(0))
    return new Decimal({ str: b.toString() })
  }

  // mult returns a new Decimal representing the product of this and
  // other. The original values are unmodified.
  mult(other: Decimal): Decimal {
    const b = this.big().mul(other.big())
    return new Decimal({ str: b.toString() })
  }

  // div returns a new Decimal representing the division of this by the
  // other. The original values are unmodified.
  div(other: Decimal): Decimal {
    const b = this.big().div(other.big())
    return new Decimal({ str: b.toString() })
  }

  // cmp compares the two Decimals. The return value is:
  //  1 if this > other
  //  0 if this == other
  // -1 if this < other
  // This function is intended for use in sorting; to compare
  // two currencies, use lt, le, ge, or gt.
  cmp(other: Decimal): number {
    return this.big().cmp(other.big())
  }

  // lt returns whether a Decimal is strictly less than another Decimal.
  lt(other: Decimal): boolean {
    return this.big().lt(other.big())
  }

  // lte returns whether a Decimal is less than or equal to another Decimal.
  lte(other: Decimal): boolean {
    return this.big().lte(other.big())
  }

  // gt returns whether a Decimal is strictly greater than another Decimal.
  gt(other: Decimal): boolean {
    return this.big().gt(other.big())
  }

  // gte returns whether a Decimal is greater than or equal to another Decimal.
  gte(other: Decimal): boolean {
    return this.big().gte(other.big())
  }

  // eq returns whether a Decimal is equal to another Decimal.
  eq(other?: Decimal): boolean {
    if (!other) {
      return false
    }
    return this.big().eq(other?.big() ?? new Big(0))
  }

  // round returns a new Decimal representing this rounded to the supplied number of
  // decimal places.
  round(decimalPlaces: number): Decimal {
    const b = this.big().round(decimalPlaces)
    return new Decimal({ str: b.toString() })
  }

  // toFixed returns a string representation of a Decimal rounded to a fixed number of decimalPlaces.
  // If decimalPlaces is undefined, the decimal is returned in normalized form with trailing zeros removed.
  toFixed(decimalPlaces?: number): string {
    const b = this.big()
    return b.toFixed(decimalPlaces)
  }

  // toString returns a string representation of a Decimal.
  toString(): string {
    const b = this.big()
    return b.toFixed()
  }

  // places returns the number of decimal places for a Decimal.
  places(): number {
    if (!this.str.includes(".")) {
      return 0
    }

    return this.str.split(".")[1].length
  }

  // int returns the integer component of d as a bigint.
  // For non-integer values, this always rounds down.
  int(): bigint {
    if (!this.str.includes(".")) {
      return BigInt(this.str)
    }
    return BigInt(this.str.split(".")[0])
  }

  // toPercentString returns a string representation of a decimal as a percentage.
  toPercentString(decimalPlaces?: number): string {
    return `${this.mult(Decimal.fromBigSource(100)).toFixed(decimalPlaces)}%`
  }

  isZero(): boolean {
    return this.big().eq(new Big(0))
  }

  isNegative(): boolean {
    return this.big().lt(new Big(0))
  }

  isPositive(): boolean {
    return this.big().gt(new Big(0))
  }

  abs(): Decimal {
    return new Decimal({ str: this.big().abs().toString() })
  }

  flipSign(): Decimal {
    return new Decimal({ str: this.big().mul(-1).toString() })
  }

  negative(): Decimal {
    return new Decimal({ str: this.big().neg().toString() })
  }
}
